import { Injectable } from '@angular/core';
import {
  AttachedFilesContent,
  DialogTextingResources,
  FileToShare,
  FileTypes,
  FileViewModel,
  PermissionTypes,
  PermissionViewModel,
  PermissionsLabels,
  ShareFilesDialogService,
} from '@dntlab/share-files-dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import AppSettings from 'src/app/common/app.settings';
import { BlockedUsersService } from 'src/app/common/services/blocked-users.service';
import { FileSharedEvent } from 'src/app/interfaces/file.interface';
import {
  FileDescriptor,
  FilePermissions,
  FoldersResponseInner,
  ListOfBasicUsers,
  DefaultService as RestApiService,
  SecurityDescriptor,
  UserBasic,
} from '../../swagger-api';
import { FilesShareDialogDelegate } from './files-share-dialog.delegate';
import { FilesValidationHandler } from './files-validation.handler';
import { UsersSuggestRecentService } from '../../services/users-suggest-recent.service';

@Injectable()
export class ShareFilesHandler {
  private usersSuggestRecentService: UsersSuggestRecentService;
  private filesShareDialogDelegate: FilesShareDialogDelegate;

  private permissionsMapper: { [key: string]: FilePermissions } = {
    [PermissionTypes.owner]: FilePermissions.Owner,
    [PermissionTypes.writer]: FilePermissions.Writer,
    [PermissionTypes.reader]: FilePermissions.Reader,
  };

  private attachedFilesContent: AttachedFilesContent = {
    fileTypeStyleMapper: {
      [FileTypes.AUDIO]: 'icon-audio',
      [FileTypes.VIDEO]: 'icon-video',
      [FileTypes.IMAGE]: 'icon-image',
      [FileTypes.DOCUMENT]: 'icon-document',
    },
  };

  public constructor(
    private fileShared$: Subject<FileSharedEvent[]>,
    private changesCompleted$: Subject<boolean>,
    private apiService: RestApiService,
    private dialogService: ShareFilesDialogService,
    private sessionService: SessionService,
    private filesValidationHandler: FilesValidationHandler,
    private translateService: TranslateService,
    private blockedUsers: BlockedUsersService,
  ) {
    this.usersSuggestRecentService = new UsersSuggestRecentService(
      this.apiService,
      this.blockedUsers,
      this.sessionService,
    );
    this.filesShareDialogDelegate = new FilesShareDialogDelegate();
  }

  public shareFiles(items: FoldersResponseInner[], allUsers: ListOfBasicUsers): void {
    this.openShareFileDialog(this.filesToFilesShareAdapter(items, allUsers));
  }

  /**
   * @description Forms textings for share-files-dialog.
   * @param { FileToShare[] } filesToShare
   * @returns { DialogTextingResources }
   */
  private getDialogTexting(filesToShare: FileToShare[]): DialogTextingResources {
    return {
      title:
        filesToShare.length > 1
          ? (this.translateService.instant('FILE_MANAGER_SHARE_FILES_DIALOG_MULTIPLE_FILES_TITLE') as string)
          : filesToShare[0].file.name,
      shareToLabel: this.translateService.instant('FILE_MANAGER_SHARE_FILES_DIALOG_SHARE_TO_LABEL') as string,
    };
  }

  /**
   * @description Returns permission labels for share-files-dialog.
   * @returns { PermissionsLabels }
   */
  private getPermissionLabels(): PermissionsLabels {
    return {
      multiple: this.translateService.instant('FILE_MANAGER_SHARE_FILES_DIALOG_MULTIPLE_PERMISSION_TITLE') as string,
      owner: this.translateService.instant('FILE_MANAGER_SHARE_FILES_DIALOG_OWNER_PERMISSION_TITLE') as string,
      reader: this.translateService.instant('FILE_MANAGER_SHARE_FILES_DIALOG_READER_PERMISSION_TITLE') as string,
      writer: this.translateService.instant('FILE_MANAGER_SHARE_FILES_DIALOG_WRITER_PERMISSION_TITLE') as string,
    };
  }

  /**
   * @description Forms FileViewModel object from response.
   * @param { FoldersResponseInner } item
   * @returns { FileViewModel }
   */
  private getFileViewModel(item: FoldersResponseInner): FileViewModel {
    return {
      entityId: item.entity['entity_id'],
      currentUserId: this.sessionService.userId,
      name: item.entity['name'],
      publicUrl: (item.entity['public_url'] as string) ? (item.entity['public_url'] as string) : '',
      isDirectory: item.entity_kind === FoldersResponseInner.EntityKindEnum.FolderDescriptor,
      type: this.filesValidationHandler.getFileType(item.entity as FileDescriptor),
    };
  }

  /**
   * @description Forms a list of PermissionViewModel objects from data.
   * @param { FoldersResponseInner } item
   * @param { UserExtended[] } users
   * @returns { PermissionViewModel[] }
   */
  private getPermissionViewModelList(item: FoldersResponseInner, users: ListOfBasicUsers): PermissionViewModel[] {
    const sharedToUsers: PermissionViewModel[] = [];
    item.security.forEach((security: SecurityDescriptor) => {
      const securityUser: UserBasic = users.items.find((user: UserBasic) => user.user_id === security.user_id);
      if (securityUser) {
        sharedToUsers.push({
          user: {
            id: securityUser.user_id,
            displayName: securityUser.displayed_name,
            userStatus: securityUser.account_status,
            email: '',
            imageUrl: securityUser.image_url || AppSettings.NO_PHOTO_DEFAULT_IMAGE,
          },
          permission: PermissionTypes[security.permission],
          isCurrentUser: securityUser.user_id === this.sessionService.userId,
        });
      }
    });
    return sharedToUsers;
  }

  /**
   * @description Converts a list of files to list of FilesShare objects for share-files dialog.
   * @param { FoldersResponseInner[] } items
   * @param { UserBasic[] } users
   * @returns { FileToShare[] }
   */
  private filesToFilesShareAdapter(items: FoldersResponseInner[], users: ListOfBasicUsers): FileToShare[] {
    const filesToShare: FileToShare[] = [];
    items.forEach((item: FoldersResponseInner) => {
      const sharedToUsers: PermissionViewModel[] = this.getPermissionViewModelList(item, users);

      if (sharedToUsers.length > 0) {
        filesToShare.push({ file: this.getFileViewModel(item), security: sharedToUsers });
      }
    });

    return filesToShare;
  }

  /**
   * @description Opens the share-files dialog with specified parameters.
   * @param { FileToShare[] } filesToShare
   */
  private openShareFileDialog(filesToShare: FileToShare[]): void {
    const subscription$: Subscription = this.getShareFilesDialog(filesToShare).subscribe(
      (result: FileToShare[] | boolean | string) => {
        subscription$.unsubscribe();
        if (
          typeof result !== 'boolean' &&
          typeof result !== 'string' &&
          typeof result !== 'undefined' &&
          result.length > 0
        ) {
          this.fileShared$.next(this.getSecurities(result));
          this.changesCompleted$.next(true);
        }
      },
    );
  }

  /**
   * @description Returns an observable of dialogShare.
   * @param { FileToShare[] } filesToShare
   */
  private getShareFilesDialog(filesToShare: FileToShare[]): Observable<string | boolean | FileToShare[]> {
    return this.dialogService.dialogShare({
      context: {
        texting: this.getDialogTexting(filesToShare),
        styles: { unshareButtonStyle: 'icon-cross' },
        filesToShare,
        shareLinkAble: false,
        permissionsLabels: this.getPermissionLabels(),
        injectedPermission: PermissionTypes.reader,
        attachedFilesContent: this.attachedFilesContent,
        hasChangesCheck: true,
        emitEmptyValue: true,
      },
      usersSuggestionDialogDelegate: this.usersSuggestRecentService,
      filesShareDialogDelegate: this.filesShareDialogDelegate,
    });
  }

  /**
   * @description Mapps filesToShare incoming data to FileSharedEvent list view.
   * @param { FileToShare[] } filesToShare
   * @returns { FileSharedEvent[] }
   */
  private getSecurities(filesToShare: FileToShare[]): FileSharedEvent[] {
    return filesToShare.map((fileToShare: FileToShare) => ({
      entity_id: fileToShare.file.entityId,
      security: fileToShare.security.map((viewModel: PermissionViewModel) => ({
        user_id: viewModel.user.id,
        permission: this.permissionsMapper[viewModel.permission],
      })),
    }));
  }
}
